import { Alert, Section, Select, Spinner } from "design-react-kit";
import SubNavigation from "../../components/Header/SubNavigation";
import { useContext, useEffect, useMemo, useState } from "react";
import PieChartComponent from "../../components/Charts/PieChartComponent";
import UserContext from "../../store/user-context";
import { useSignIn, useSignOut } from "react-auth-kit";
import { useLocation, useNavigate } from "react-router-dom";
import {
  createQueryString,
  sendApiRequest,
} from "../../services/Base/base-service";
import secureLocalStorage from "react-secure-storage";

const transformData = (input) => {
  return input.map((service) => {
    const totals = service.statistics.reduce(
      (acc, item) => {
        acc.sent += item.sent;
        acc.digitalSuccess += item.digitalSuccess;
        acc.analogicalSuccess += item.analogicalSuccess;
        acc.paid += item.paid;
        return acc;
      },
      { sent: 0, digitalSuccess: 0, analogicalSuccess: 0, paid: 0 },
    );

    return {
      code: service.code,
      ...totals,
    };
  });
};

const StatisticsSummary = () => {
  const [startYear, setStartYear] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [statisticsData, setStatisticsData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const userContext = useContext(UserContext);

  const signIn = useSignIn();
  const signOut = useSignOut();
  const navigate = useNavigate();

  const { state } = useLocation();

  const filteredData = useMemo(
    () => (statisticsData ?? []).filter((item) => item.sent > 0),
    [statisticsData],
  );

  const totalSent = useMemo(
    () => filteredData.reduce((sum, item) => sum + item.sent, 0),
    [filteredData],
  );

  const paidData = useMemo(() => {
    const totalPaid = filteredData.reduce((sum, item) => sum + item.paid, 0);
    return [
      { code: "Pagate", sent: totalPaid },
      { code: "Non pagate", sent: totalSent - totalPaid },
    ];
  }, [filteredData, totalSent]);

  const deliveryData = useMemo(() => {
    const totalDigital = filteredData.reduce((sum, item) => sum + item.digitalSuccess, 0);
    const totalAnalogical = filteredData.reduce((sum, item) => sum + item.analogicalSuccess, 0);
    return [
      { code: "Digitale", sent: totalDigital },
      { code: "Analogico", sent: totalAnalogical },
      { code: "Non consegnate", sent: Math.max(totalSent - totalDigital - totalAnalogical, 0) },
    ];
  }, [filteredData, totalSent]);

  let currentYear = new Date().getFullYear();

  const years = Array.from({ length: currentYear - startYear + 1 }, (_, i) => {
    const year = startYear + i;
    return { value: year, label: year };
  });

  useEffect(() => {
    if (userContext.selectedClientFiscalCode !== null) {
      if (state === null) {
        getStatisticsData(currentYear);
        getFirstStartYear();
        setSelectedYear({ value: currentYear, label: currentYear });
      } else {
        getStatisticsData(state);
        getFirstStartYear();
        setSelectedYear({ value: state, label: state });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userContext, currentYear]);

  const getStatisticsData = (year) => {
    setError(null);
    setIsLoading(true);
    sendApiRequest(
      `Statistics/GetSummary?Year=${year}&FiscalCode=${userContext.selectedClientFiscalCode}`,
      "GET",
      true,
      null,
      signIn,
      signOut,
      navigate,
      null,
      userContext,
    ).then((response) => {
      if (response !== undefined) {
        if (response.errors !== null) {
          setError(response.errors);
        } else {
          const newData = transformData(response.data.clients);
          setStatisticsData(newData);
        }
        setIsLoading(false);
      }
    });
  };

  const getFirstStartYear = () => {
    setError(null);
    setIsLoading(true);
    sendApiRequest(
      `Statistics/GetFirstStartYear`,
      "GET",
      true,
      { fiscalCode: userContext.selectedClientFiscalCode, flowId: null },
      signIn,
      signOut,
      navigate,
      createQueryString,
      userContext,
    ).then((response) => {
      if (response !== undefined) {
        if (response.errors !== null) {
          setError(response.errors);
        } else {
          secureLocalStorage.setItem("year", response.data);
          setStartYear(response.data);
        }
        setIsLoading(false);
      }
    });
  };

  const handleChange = (selectedOption) => {
    setSelectedYear(selectedOption);
    secureLocalStorage.setItem("year", selectedOption.value);
    getStatisticsData(selectedOption.value);
  };

  const showPieText = () => {
    if (selectedYear.value === currentYear) {
      return "Notifiche inviate nell'anno in corso";
    } else if (selectedYear.value === currentYear - 1) {
      return "Notifiche inviate nell'anno precedente";
    } else {
      return `Notifiche inviate nell'anno ${selectedYear.label}`;
    }
  };

  const showPaidText = () => {
    if (selectedYear.value === currentYear) {
      return "Rapporto inviate/concluse nell'anno in corso";
    } else if (selectedYear.value === currentYear - 1) {
      return "Rapporto inviate/concluse nell'anno precedente";
    } else {
      return `Rapporto inviate/concluse nell'anno ${selectedYear.label}`;
    }
  };

  const showDeliveryText = () => {
    if (selectedYear.value === currentYear) {
      return "Modalità di consegna nell'anno in corso";
    } else if (selectedYear.value === currentYear - 1) {
      return "Modalità di consegna nell'anno precedente";
    } else {
      return `Modalità di consegna nell'anno ${selectedYear.label}`;
    }
  };

  return (
    <>
      <SubNavigation
        page="statistics"
        year={selectedYear?.value}
        showStatisticsDetails={filteredData.length > 0}
      />
      <div className="w-100 d-flex justify-content-center mt-3">
        <div className="bootstrap-select-wrapper mb-5 mt-5 col-sm-3">
          <label htmlFor="municipalities">
            Seleziona l'anno per cui vuoi vedere i risultati
          </label>
          <Select
            id="municipalities"
            placeholder="Selezione il comune"
            aria-label="Seleziona"
            value={selectedYear}
            options={years}
            onChange={handleChange}
          />
        </div>
      </div>
      {error !== null && error.length !== undefined && (
        <Alert color="danger" className="mx-auto">
          {error.map((item) => (
            <p key={item}>{item}</p>
          ))}
        </Alert>
      )}
      {isLoading && (
        <p
          style={{
            height: "50px",
            position: "relative",
            marginBottom: "3rem",
          }}
        >
          <Spinner double active />
        </p>
      )}
      {selectedYear !== null && filteredData.length > 0 && (
          <Section>
            <div className="d-flex flex-column align-items-center w-100">
              <div className="d-flex flex-column align-items-center mb-4">
                <h5>{showPieText()}</h5>
                <PieChartComponent
                  year={selectedYear.value}
                  data={filteredData}
                  labelX="44%"
                />
              </div>
              <div className="d-flex justify-content-around w-100 mb-5">
                <div className="d-flex flex-column align-items-center">
                  <h5>{showPaidText()}</h5>
                  <PieChartComponent
                    year={selectedYear.value}
                    data={paidData}
                    colors={["#00994f", "#888888"]}
                    chartWidth={550}
                    chartHeight={320}
                    innerRadius={60}
                    outerRadius={120}
                    labelX="36%"
                  />
                </div>
                <div className="d-flex flex-column align-items-center">
                  <h5>{showDeliveryText()}</h5>
                  <PieChartComponent
                    year={selectedYear.value}
                    data={deliveryData}
                    colors={["#0a80f5", "#00994f", "#888888"]}
                    chartWidth={550}
                    chartHeight={320}
                    innerRadius={60}
                    outerRadius={120}
                    labelX="35%"
                  />
                </div>
              </div>
            </div>
          </Section>
        )}
      {filteredData.length === 0 && (
        <p>
          <Alert className="w-50 mx-auto" color="info">
            Nessun risultato trovato!
          </Alert>
        </p>
      )}
    </>
  );
};

export default StatisticsSummary;
